import { useState, useEffect, useRef } from "react";
import {
  Modal,
  ScrollView,
  View,
  Text,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import CurrencyInput from "react-native-currency-input";
import AdminInput from "../../../../components/AdminInput";
import AdminSwitch from "../../../../components/AdminSwitch";
import { CatalogVitrineFields } from "../../../../components/CatalogVitrineFields";
import ProductCatalogFilters from "../../../../components/ProductCatalogFilters";
import AdminCheckbox from "../../../../components/AdminCheckbox";
import AdminButton from "../../../../components/AdminButton";
import AdminAlert from "../../../../components/AdminAlert";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../../config/theme/adminColors";
import {
  ModalBackground,
  ModalContainer,
  Header,
  CloseButton,
  Content,
} from "./../style";
import {
  SectionCard,
  Field,
  FieldLabel,
  FieldError,
  Hint,
  Row,
  Col,
  Badge,
  InfoBanner,
} from "../formStyles";
import {
  CheckoutLinksCard,
  buildPlanCheckoutUrl,
  useCheckoutDomain,
} from "./CheckoutLinks";
import { hasOutsideAboutPage } from "../../../../config/data";
import { useCustomTheme } from "../../../../contexts/useCustomTheme";
import * as productService from "./../service/product-service";
import { firestore } from "../../../../services/firebase";
import { collection, getDocs, query, orderBy, doc, updateDoc } from "firebase/firestore";
import { Ionicons, Feather } from "@expo/vector-icons";

// --------------------
// Helpers
// --------------------
const parseCurrencyToNumber = (value) => {
  if (typeof value === "number") return value;
  if (!value) return 0;

  const raw = String(value)
    .replace(/R\$/g, "")
    .replace(/\s/g, "")
    .replace(/[^0-9.,]/g, "");

  if (!raw) return 0;

  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const extractCurrencyFromText = (text) => {
  if (!text) return "";
  const s = String(text);
  const match = s.match(/R\$\s*\d[\d.,]*/);
  return match ? match[0].trim() : "";
};

// tenta pegar uma linha que tenha "x" (parcelado). Se não achar, cai na primeira.
const extractInstallmentValueFromInfos = (infosArray) => {
  if (!Array.isArray(infosArray) || infosArray.length === 0) return "";
  const candidate = infosArray.find(
    (l) =>
      typeof l === "string" &&
      /(\d+)\s*x/i.test(l) &&
      /R\$\s*\d/.test(l)
  );
  return extractCurrencyFromText(candidate || infosArray[0]);
};

const normalizeLines = (arr) =>
  Array.isArray(arr)
    ? arr.map((l) => String(l ?? "").trim()).filter(Boolean)
    : [];

// “assinatura” do que está selecionado no tree pra evitar updates desnecessários
const buildSelectedCardsSignature = (t) => {
  const keys = [];
  (t || []).forEach((cat) => {
    (cat.cards || []).forEach((card) => {
      if (card.selected) keys.push(`${cat.id}::${card.id}`);
    });
  });
  keys.sort();
  return keys.join("|");
};

// ✅ Ajuste aqui se sua coleção de planos tiver outro nome
const PLANS_COLLECTION = "plans";

// A cópia dos links de checkout agora é do próprio card (CheckoutLinks), com
// feedback de "Copiado!" — por isso o modal não recebe mais um `copy` de fora.
const EditStripePlanModal = ({ isVisible, onClose, onEdit, selectedPlan, user }) => {
  const vitrineRef = useRef(null);
  const [vitrineFeedback, setVitrineFeedback] = useState(null);
  const { customInfo: customizationInfo } = useCustomTheme();

  const [loadingSave, setLoadingSave] = useState(false);

  // campos gerais
  const [titlePlanValue, setTitlePlanValue] = useState({ value: "", error: "" });
  const [salesPageUrl, setSalesPageUrl] = useState(customizationInfo?.salesPageUrl || "");
  const [isActive, setIsActive] = useState(true);

  const [mockedPlanId, setMockedPlanId] = useState("");
  const [mockedProductIdMonthly, setMockedProductIdMonthly] = useState("");
  const [mockedProductIdQuarterly, setMockedProductIdQuarterly] = useState("");
  const [mockedProductIdSemiannual, setMockedProductIdSemiannual] = useState("");
  const [mockedProductIdYearly, setMockedProductIdYearly] = useState("");

  // controles de sessão
  const [editPricesEnabled, setEditPricesEnabled] = useState(false);
  const [editInfosEnabled, setEditInfosEnabled] = useState(false);

  // trial
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState("7");
  const [trialError, setTrialError] = useState("");

  // preços
  const [pricePlanMonthly, setPricePlanMonthly] = useState({ value: "", error: "" });
  const [pricePlanStripeMonthly, setPricePlanStripeMonthly] = useState();
  const [valuePlanWithRSMonthly, setValuePlanWithRSMonthly] = useState("");

  const [pricePlanQuarterlyAtSight, setPricePlanQuarterlyAtSight] = useState({ value: "", error: "" });
  const [pricePlanStripeQuarterlyAtSight, setPricePlanStripeQuarterlyAtSight] = useState();
  const [valuePlanWithRSQuarterlyAtSight, setValuePlanWithRSQuarterlyAtSight] = useState("");

  const [pricePlanQuarterly, setPricePlanQuarterly] = useState({ value: "", error: "" });
  const [pricePlanStripeQuarterly, setPricePlanStripeQuarterly] = useState();
  const [valuePlanWithRSQuarterly, setValuePlanWithRSQuarterly] = useState("");

  const [pricePlanSemiannualAtSight, setPricePlanSemiannualAtSight] = useState({ value: "", error: "" });
  const [pricePlanStripeSemiannualAtSight, setPricePlanStripeSemiannualAtSight] = useState();
  const [valuePlanWithRSSemiannualAtSight, setValuePlanWithRSSemiannualAtSight] = useState("");

  const [pricePlanSemiannual, setPricePlanSemiannual] = useState({ value: "", error: "" });
  const [pricePlanStripeSemiannual, setPricePlanStripeSemiannual] = useState();
  const [valuePlanWithRSSemiannual, setValuePlanWithRSSemiannual] = useState("");

  const [pricePlanYearlyAtSight, setPricePlanYearlyAtSight] = useState({ value: "", error: "" });
  const [pricePlanStripeYearlyAtSight, setPricePlanStripeYearlyAtSight] = useState();
  const [valuePlanWithRSYearlyAtSight, setValuePlanWithRSYearlyAtSight] = useState("");

  const [pricePlanYearly, setPricePlanYearly] = useState({ value: "", error: "" });
  const [pricePlanStripeYearly, setPricePlanStripeYearly] = useState();
  const [valuePlanWithRSYearly, setValuePlanWithRSYearly] = useState("");

  const [canEditPrices, setCanEditPrices] = useState(false);
  const [originalStripePrices, setOriginalStripePrices] = useState(null);

  const [expandedCategoryIds, setExpandedCategoryIds] = useState([]);
  const [tree, setTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingInitialLinks, setLoadingInitialLinks] = useState(true);
  const [initialCardsSignature, setInitialCardsSignature] = useState("");

  const [alertState, setAlertState] = useState({ visible: false, message: null });

  const [isToggling, setIsToggling] = useState(false);

  // Os links de checkout são montados na abertura do modal: o domínio é a única
  // informação que falta (os productIds já vêm no plano), então não faz sentido
  // exigir um clique em "Carregar URLs" antes de mostrar o que já se sabe.
  const {
    domain: checkoutDomain,
    loading: loadingCheckoutDomain,
    hasError: checkoutDomainError,
    reload: reloadCheckoutDomain,
  } = useCheckoutDomain(isVisible && hasOutsideAboutPage);

  const hasMonthly = Array.isArray(selectedPlan?.infosMonthly) && selectedPlan?.infosMonthly?.length > 0;
  const hasQuarterly = Array.isArray(selectedPlan?.infosQuarterly) && selectedPlan?.infosQuarterly?.length > 0;
  const hasSemiannual = Array.isArray(selectedPlan?.infosSemiannual) && selectedPlan?.infosSemiannual?.length > 0;
  const hasYearly = Array.isArray(selectedPlan?.infosYearly) && selectedPlan?.infosYearly?.length > 0;

  // descrições por frequência
  const [infosMonthlyLines, setInfosMonthlyLines] = useState([]);
  const [infosQuarterlyLines, setInfosQuarterlyLines] = useState([]);
  const [infosSemiannualLines, setInfosSemiannualLines] = useState([]);
  const [infosYearlyLines, setInfosYearlyLines] = useState([]);

  const [expandedInfos, setExpandedInfos] = useState({
    monthly: true,
    quarterly: false,
    semiannual: false,
    yearly: false,
  });

  const showAlert = (message) => setAlertState({ visible: true, message });

  useEffect(() => {
    (async () => {
      setLoadingTree(true);
      try {
        const catsSnap = await getDocs(
          query(collection(firestore, "categories"), orderBy("order", "asc"))
        );

        const tmp = [];

        for (let catDoc of catsSnap.docs) {
          const cat = {
            id: catDoc.id,
            title: catDoc.data().title,
            selected: false,
            cards: [],
          };

          const cardsSnap = await getDocs(
            query(collection(firestore, "categories", cat.id, "cards"), orderBy("order", "asc"))
          );

          cardsSnap.docs.forEach((cardDoc) => {
            const data = cardDoc.data();

            const plans = Array.isArray(data.plans) ? data.plans : [];

            const productIds = Array.isArray(data.plans)
              ? data.plans.map((plan) => plan.productId).filter(Boolean)
              : [];

            cat.cards.push({
              id: cardDoc.id,
              title: data.title,
              plans,
              productIds,
              selected: false,
            });
          });

          tmp.push(cat);
        }

        setTree(tmp);
      } catch (err) {
        console.error("Erro ao carregar categorias:", err);
      } finally {
        setLoadingTree(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPlan) return;
    if (!tree.length) return;
    if (!loadingInitialLinks) return;

    const selectedProductIds = Object.values(selectedPlan.productIds || {}).filter(Boolean);

    const updated = tree.map((cat) => {
      const hasAny = cat.cards.some((card) =>
        card.productIds?.some((pid) => selectedProductIds.includes(pid))
      );

      return {
        ...cat,
        selected: hasAny,
        cards: cat.cards.map((card) => ({
          ...card,
          selected: card.productIds?.some((pid) => selectedProductIds.includes(pid)),
        })),
      };
    });

    setTree(updated);
    setInitialCardsSignature(buildSelectedCardsSignature(updated));
    setLoadingInitialLinks(false);
  }, [tree, selectedPlan, loadingInitialLinks]);

  useEffect(() => {
    if (!selectedPlan) return;

    setMockedPlanId(selectedPlan.id || "");
    setMockedProductIdMonthly(selectedPlan.productIds?.monthly || "");
    setMockedProductIdQuarterly(selectedPlan.productIds?.quarterly || "");
    setMockedProductIdSemiannual(selectedPlan.productIds?.semiannual || "");
    setMockedProductIdYearly(selectedPlan.productIds?.yearly || "");

    setTitlePlanValue({ value: selectedPlan.title || "", error: "" });
    setIsActive(!selectedPlan.archived);
    setSalesPageUrl(selectedPlan.salesPageUrl || customizationInfo?.salesPageUrl || "");

    setInfosMonthlyLines(normalizeLines(selectedPlan.infosMonthly || []));
    setInfosQuarterlyLines(normalizeLines(selectedPlan.infosQuarterly || []));
    setInfosSemiannualLines(normalizeLines(selectedPlan.infosSemiannual || []));
    setInfosYearlyLines(normalizeLines(selectedPlan.infosYearly || []));

    // Prefill strings do Firestore
    const monthlyStr = selectedPlan.monthlyPrice || "";
    const quarterlyAtSightStr = selectedPlan.quarterlyPrice || "";
    const semiannualAtSightStr = selectedPlan.semiannualPrice || "";
    const yearlyAtSightStr = selectedPlan.yearlyPrice || "";

    const quarterlyInstallStr = extractInstallmentValueFromInfos(selectedPlan.infosQuarterly);
    const semiannualInstallStr = extractInstallmentValueFromInfos(selectedPlan.infosSemiannual);
    const yearlyInstallStr = extractInstallmentValueFromInfos(selectedPlan.infosYearly);

    const monthlyVal = parseCurrencyToNumber(monthlyStr);
    const quarterlyAtSightVal = parseCurrencyToNumber(quarterlyAtSightStr);
    const semiannualAtSightVal = parseCurrencyToNumber(semiannualAtSightStr);
    const yearlyAtSightVal = parseCurrencyToNumber(yearlyAtSightStr);

    const quarterlyInstallVal = parseCurrencyToNumber(quarterlyInstallStr);
    const semiannualInstallVal = parseCurrencyToNumber(semiannualInstallStr);
    const yearlyInstallVal = parseCurrencyToNumber(yearlyInstallStr);

    setPricePlanMonthly({ value: monthlyVal || "", error: "" });
    setPricePlanStripeMonthly(monthlyVal ? Math.round(monthlyVal * 100) : undefined);
    setValuePlanWithRSMonthly(monthlyStr || "");

    setPricePlanQuarterlyAtSight({ value: quarterlyAtSightVal || "", error: "" });
    setPricePlanStripeQuarterlyAtSight(quarterlyAtSightVal ? Math.round(quarterlyAtSightVal * 100) : undefined);
    setValuePlanWithRSQuarterlyAtSight(quarterlyAtSightStr || "");

    setPricePlanQuarterly({ value: quarterlyInstallVal || "", error: "" });
    setPricePlanStripeQuarterly(quarterlyInstallVal ? Math.round(quarterlyInstallVal * 100) : undefined);
    setValuePlanWithRSQuarterly(quarterlyInstallStr || "");

    setPricePlanSemiannualAtSight({ value: semiannualAtSightVal || "", error: "" });
    setPricePlanStripeSemiannualAtSight(semiannualAtSightVal ? Math.round(semiannualAtSightVal * 100) : undefined);
    setValuePlanWithRSSemiannualAtSight(semiannualAtSightStr || "");

    setPricePlanSemiannual({ value: semiannualInstallVal || "", error: "" });
    setPricePlanStripeSemiannual(semiannualInstallVal ? Math.round(semiannualInstallVal * 100) : undefined);
    setValuePlanWithRSSemiannual(semiannualInstallStr || "");

    setPricePlanYearlyAtSight({ value: yearlyAtSightVal || "", error: "" });
    setPricePlanStripeYearlyAtSight(yearlyAtSightVal ? Math.round(yearlyAtSightVal * 100) : undefined);
    setValuePlanWithRSYearlyAtSight(yearlyAtSightStr || "");

    setPricePlanYearly({ value: yearlyInstallVal || "", error: "" });
    setPricePlanStripeYearly(yearlyInstallVal ? Math.round(yearlyInstallVal * 100) : undefined);
    setValuePlanWithRSYearly(yearlyInstallStr || "");

    setOriginalStripePrices({
      monthly: monthlyVal ? Math.round(monthlyVal * 100) : undefined,
      quarterlyAtSight: quarterlyAtSightVal ? Math.round(quarterlyAtSightVal * 100) : undefined,
      quarterly: quarterlyInstallVal ? Math.round(quarterlyInstallVal * 100) : undefined,
      semiannualAtSight: semiannualAtSightVal ? Math.round(semiannualAtSightVal * 100) : undefined,
      semiannual: semiannualInstallVal ? Math.round(semiannualInstallVal * 100) : undefined,
      yearlyAtSight: yearlyAtSightVal ? Math.round(yearlyAtSightVal * 100) : undefined,
      yearly: yearlyInstallVal ? Math.round(yearlyInstallVal * 100) : undefined,
    });

    setCanEditPrices(!!user?.isAdmin);

    // Ao abrir, começa fechado
    setEditPricesEnabled(false);
    setEditInfosEnabled(false);

    // Prefill trial
    const rawTrial = selectedPlan?.trialDays ?? selectedPlan?.trial ?? selectedPlan?.trial_period_days;
    const nTrial = Number(rawTrial);

    if (rawTrial === null || rawTrial === undefined || rawTrial === "") {
      setTrialEnabled(true);
      setTrialDays("7");
    } else if (Number.isFinite(nTrial) && nTrial > 0) {
      setTrialEnabled(true);
      setTrialDays(String(nTrial));
    } else {
      setTrialEnabled(false);
      setTrialDays("0");
    }

    setTrialError("");
  }, [selectedPlan, customizationInfo?.salesPageUrl, user?.isAdmin]);

  const getFrequencyLabel = (pid) => {
    if (pid === mockedProductIdMonthly) return "Mensal";
    if (pid === mockedProductIdQuarterly) return "Trimestral";
    if (pid === mockedProductIdSemiannual) return "Semestral";
    if (pid === mockedProductIdYearly) return "Anual";
    return "";
  };

  const resetFormFields = () => {
    setPricePlanMonthly({ value: "", error: "" });
    setPricePlanStripeMonthly(undefined);
    setValuePlanWithRSMonthly("");

    setPricePlanQuarterlyAtSight({ value: "", error: "" });
    setPricePlanStripeQuarterlyAtSight(undefined);
    setValuePlanWithRSQuarterlyAtSight("");
    setPricePlanQuarterly({ value: "", error: "" });
    setPricePlanStripeQuarterly(undefined);
    setValuePlanWithRSQuarterly("");

    setPricePlanSemiannualAtSight({ value: "", error: "" });
    setPricePlanStripeSemiannualAtSight(undefined);
    setValuePlanWithRSSemiannualAtSight("");
    setPricePlanSemiannual({ value: "", error: "" });
    setPricePlanStripeSemiannual(undefined);
    setValuePlanWithRSSemiannual("");

    setPricePlanYearlyAtSight({ value: "", error: "" });
    setPricePlanStripeYearlyAtSight(undefined);
    setValuePlanWithRSYearlyAtSight("");
    setPricePlanYearly({ value: "", error: "" });
    setPricePlanStripeYearly(undefined);
    setValuePlanWithRSYearly("");

    setMockedPlanId("");
    setMockedProductIdMonthly("");
    setMockedProductIdQuarterly("");
    setMockedProductIdSemiannual("");
    setMockedProductIdYearly("");

    setTitlePlanValue({ value: "", error: "" });
    setSalesPageUrl("");
    setIsActive(true);
    setAlertState({ visible: false, message: null });
    setCanEditPrices(false);
    setOriginalStripePrices(null);

    setInfosMonthlyLines([]);
    setInfosQuarterlyLines([]);
    setInfosSemiannualLines([]);
    setInfosYearlyLines([]);

    setExpandedInfos({
      monthly: true,
      quarterly: false,
      semiannual: false,
      yearly: false,
    });

    setEditPricesEnabled(false);
    setEditInfosEnabled(false);

    setLoadingInitialLinks(true);
    setInitialCardsSignature("");

    setTrialEnabled(true);
    setTrialDays("7");
    setTrialError("");
  };

  const handleToggleActive = async (val) => {
    if (!mockedPlanId) {
      showAlert("ID do plano não encontrado. Reabra o modal e tente novamente.");
      return;
    }

    const newArchived = !val;

    setIsActive(val);
    setIsToggling(true);

    try {
      const res = await productService.editPlanOnStripe({
        mockedPlanId,
        archived: newArchived,
        user,
        addNewPlansEnabled: false,
      });

      if (!res?.data?.success) throw new Error(res?.data?.error || "Erro no servidor");

      showAlert(val ? "✅ Plano ativado com sucesso!" : "✅ Plano arquivado com sucesso!");
      if (onEdit) await onEdit();
    } catch (err) {
      console.error("Erro ao atualizar archived:", err);
      showAlert("❌ Erro ao atualizar status do plano: " + (err?.message || err));
      setIsActive(!val);
    } finally {
      setIsToggling(false);
    }
  };

  const validateInstallmentsRule = () => {
    if (pricePlanQuarterlyAtSight.value && pricePlanQuarterly.value) {
      const atSight = Number(pricePlanQuarterlyAtSight.value);
      const parcelTotal = Number(pricePlanQuarterly.value) * 3;
      if (atSight > parcelTotal) {
        showAlert("Valor Trimestral à vista não pode ser maior que o total parcelado (3x).");
        return false;
      }
    }
    if (pricePlanSemiannualAtSight.value && pricePlanSemiannual.value) {
      const atSight = Number(pricePlanSemiannualAtSight.value);
      const parcelTotal = Number(pricePlanSemiannual.value) * 6;
      if (atSight > parcelTotal) {
        showAlert("Valor Semestral à vista não pode ser maior que o total parcelado (6x).");
        return false;
      }
    }
    if (pricePlanYearlyAtSight.value && pricePlanYearly.value) {
      const atSight = Number(pricePlanYearlyAtSight.value);
      const parcelTotal = Number(pricePlanYearly.value) * 12;
      if (atSight > parcelTotal) {
        showAlert("Valor Anual à vista não pode ser maior que o total parcelado (12x).");
        return false;
      }
    }
    return true;
  };

  const validateTrialConfig = () => {
    if (!trialEnabled) return true;

    const n = Number(trialDays);
    if (!Number.isInteger(n) || n < 1 || n > 730) {
      setTrialError("Informe um número inteiro entre 1 e 730 dias.");
      showAlert("Por favor, informe um período de teste válido (1–730 dias).");
      return false;
    }

    setTrialError("");
    return true;
  };

  const updatePlanOnFirestoreOnly = async (addNewPlansEnabled) => {
    if (!mockedPlanId) throw new Error("PLAN_ID_MISSING");

    const payload = {
      title: titlePlanValue.value,
      salesPageUrl: salesPageUrl || "",
      archived: !isActive,

      ...(editInfosEnabled && {
        infosMonthly: normalizeLines(infosMonthlyLines),
        infosQuarterly: normalizeLines(infosQuarterlyLines),
        infosSemiannual: normalizeLines(infosSemiannualLines),
        infosYearly: normalizeLines(infosYearlyLines),
      }),

      // ✅ trialDays só atualiza se estiver criando novos preços
      ...(addNewPlansEnabled && {
        trialDays: trialEnabled ? String(Number(trialDays)) : null,
      }),
    };

    await updateDoc(doc(firestore, PLANS_COLLECTION, mockedPlanId), payload);
  };

  const handleEdit = async () => {
    setLoadingSave(true);
    setVitrineFeedback(null);

    try {
      if (vitrineRef.current?.isBusy?.()) {
        setVitrineFeedback({
          variant: "error",
          message: "Aguarde o envio da imagem da vitrine terminar antes de salvar.",
        });
        return;
      }

      if (vitrineRef.current?.saveVitrine) {
        const vitrineResult = await vitrineRef.current.saveVitrine();
        if (!vitrineResult?.ok) {
          setVitrineFeedback(vitrineResult);
          return;
        }
      }

      if (!titlePlanValue.value || titlePlanValue.value.trim() === "") {
        setTitlePlanValue({ ...titlePlanValue, error: "Nome do plano é obrigatório" });
        showAlert("Por favor, informe o nome do plano.");
        return;
      }

      const addNewPlansEnabled = !!(editPricesEnabled && canEditPrices);
      const wantsStripeUpdate = addNewPlansEnabled;
      const wantsInfosUpdate = editInfosEnabled;

      if (wantsStripeUpdate) {
        if (!validateInstallmentsRule()) return;
      }

      const currentCardsSignature = buildSelectedCardsSignature(tree);
      const cardsChanged = currentCardsSignature !== (initialCardsSignature || "");

      const normalize = (s) => String(s ?? "").trim();
      const originalActive = !selectedPlan?.archived;

      const generalChanged =
        normalize(titlePlanValue.value) !== normalize(selectedPlan?.title) ||
        normalize(salesPageUrl) !== normalize(selectedPlan?.salesPageUrl || customizationInfo?.salesPageUrl || "") ||
        isActive !== originalActive;

      if (!wantsStripeUpdate && !wantsInfosUpdate && !cardsChanged && !generalChanged) {
        showAlert("Nenhuma alteração para salvar. Altere nome/página, vínculos, descrições ou valores.");
        return;
      }

      // 1) Stripe OU Firestore-only
      if (wantsStripeUpdate) {
        const changed = {
          monthly: pricePlanStripeMonthly !== originalStripePrices?.monthly,
          quarterlyAtSight: pricePlanStripeQuarterlyAtSight !== originalStripePrices?.quarterlyAtSight,
          quarterly: pricePlanStripeQuarterly !== originalStripePrices?.quarterly,
          semiannualAtSight: pricePlanStripeSemiannualAtSight !== originalStripePrices?.semiannualAtSight,
          semiannual: pricePlanStripeSemiannual !== originalStripePrices?.semiannual,
          yearlyAtSight: pricePlanStripeYearlyAtSight !== originalStripePrices?.yearlyAtSight,
          yearly: pricePlanStripeYearly !== originalStripePrices?.yearly,
        };

        const anyPriceChanged = Object.values(changed).some(Boolean);

        if (addNewPlansEnabled && !anyPriceChanged) {
          showAlert("Você marcou 'Quero adicionar novos preços', mas não alterou nenhum valor. Ajuste algum preço para criar novos preços na Stripe.");
          return;
        }

        const stripePricesPayload = {
          pricePlanStripeMonthly: changed.monthly ? pricePlanStripeMonthly : undefined,
          valuePlanWithRSMonthly: changed.monthly ? valuePlanWithRSMonthly : undefined,

          pricePlanStripeQuarterlyAtSight: changed.quarterlyAtSight ? pricePlanStripeQuarterlyAtSight : undefined,
          valuePlanWithRSQuarterlyAtSight: changed.quarterlyAtSight ? valuePlanWithRSQuarterlyAtSight : undefined,

          pricePlanStripeQuarterly: changed.quarterly ? pricePlanStripeQuarterly : undefined,
          valuePlanWithRSQuarterly: changed.quarterly ? valuePlanWithRSQuarterly : undefined,

          pricePlanStripeSemiannualAtSight: changed.semiannualAtSight ? pricePlanStripeSemiannualAtSight : undefined,
          valuePlanWithRSSemiannualAtSight: changed.semiannualAtSight ? valuePlanWithRSSemiannualAtSight : undefined,

          pricePlanStripeSemiannual: changed.semiannual ? pricePlanStripeSemiannual : undefined,
          valuePlanWithRSSemiannual: changed.semiannual ? valuePlanWithRSSemiannual : undefined,

          pricePlanStripeYearlyAtSight: changed.yearlyAtSight ? pricePlanStripeYearlyAtSight : undefined,
          valuePlanWithRSYearlyAtSight: changed.yearlyAtSight ? valuePlanWithRSYearlyAtSight : undefined,

          pricePlanStripeYearly: changed.yearly ? pricePlanStripeYearly : undefined,
          valuePlanWithRSYearly: changed.yearly ? valuePlanWithRSYearly : undefined,

          originalStripePrices,
          changed,
        };

        const infosPayload = wantsInfosUpdate
          ? {
            infosPlan: normalizeLines(infosMonthlyLines),
            infosPlanQuarterly: normalizeLines(infosQuarterlyLines),
            infosPlanSemiannual: normalizeLines(infosSemiannualLines),
            infosPlanYearly: normalizeLines(infosYearlyLines),
          }
          : {};

        // ✅ trial só valida/aplica quando está criando novos preços
        if (addNewPlansEnabled && trialEnabled) {
          if (!validateTrialConfig()) return;
        }

        const trial = addNewPlansEnabled && trialEnabled ? Number(trialDays) : 0;

        const myData = {
          addNewPlansEnabled, // ✅ ESSENCIAL

          trial,
          ...stripePricesPayload,
          ...infosPayload,

          productIdMonthly: mockedProductIdMonthly,
          productIdQuarterly: mockedProductIdQuarterly,
          productIdSemiannual: mockedProductIdSemiannual,
          productIdYearly: mockedProductIdYearly,

          mockedPlanId,
          user,
          titlePlanValue: titlePlanValue.value,
          salesPageUrl,
          archived: !isActive,
        };

        await productService.editPlanOnStripe(myData);
      } else {
        await updatePlanOnFirestoreOnly(false);
      }

      // 2) Vínculos
      if (cardsChanged) {
        const selectedProductIds = [
          mockedProductIdMonthly,
          mockedProductIdQuarterly,
          mockedProductIdSemiannual,
          mockedProductIdYearly,
        ].filter(Boolean);

        const selectedCards = [];
        tree.forEach((cat) => {
          cat.cards.forEach((card) => {
            if (card.selected) {
              selectedCards.push({
                categoryId: cat.id,
                cardId: card.id,
              });
            }
          });
        });

        for (const cat of tree) {
          const cardsSnap = await getDocs(collection(firestore, "categories", cat.id, "cards"));

          for (const cardDoc of cardsSnap.docs) {
            const cardId = cardDoc.id;
            const categoryId = cat.id;

            const ref = doc(firestore, "categories", categoryId, "cards", cardId);

            const cardData = cardDoc.data();
            const cardIsSelected = selectedCards.some(
              (c) => c.cardId === cardId && c.categoryId === categoryId
            );

            const existingPlans = cardData.plans || [];

            if (cardIsSelected) {
              const newPlansArray = [...existingPlans];

              for (const pid of selectedProductIds) {
                const alreadyExists = existingPlans.some((p) => p.productId === pid);
                if (!alreadyExists) {
                  const frequencyLabel = getFrequencyLabel(pid);
                  newPlansArray.push({
                    id: mockedPlanId,
                    productId: pid,
                    selected: true,
                    title: `${titlePlanValue.value} ${frequencyLabel}`.trim(),
                  });
                }
              }

              await updateDoc(ref, { plans: newPlansArray });
            } else {
              const newPlansArray = existingPlans.filter((plan) => !selectedProductIds.includes(plan.productId));
              await updateDoc(ref, { plans: newPlansArray });
            }
          }
        }
      }

      showAlert("✅ Alterações salvas com sucesso!");
      resetFormFields();
      if (onEdit) await onEdit();
      onClose();
    } catch (error) {
      console.error("Erro ao editar plano:", error);
      showAlert("Erro ao salvar: " + (error?.message || String(error)));
    } finally {
      setLoadingSave(false);
    }
  };


  // Editor das linhas de descrição de uma frequência.
  const renderLinesEditor = (lines, setLines) => (
    <View style={{ marginTop: 10 }}>
      {lines.map((line, idx) => (
        <View key={`${idx}`} style={styles.lineCard}>
          <View style={styles.lineHeader}>
            <Text style={styles.lineTitle}>Item {idx + 1}</Text>
            <Pressable
              onPress={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
              hitSlop={6}
              style={({ hovered }) => [
                styles.lineRemove,
                hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
              ]}
            >
              <Ionicons name="trash-outline" size={14} color={ADMIN_COLORS.danger} />
              <Text style={styles.lineRemoveText}>Remover</Text>
            </Pressable>
          </View>

          <AdminInput
            placeholder="Digite a linha da descrição..."
            value={line}
            onChangeText={(txt) => {
              setLines((prev) => prev.map((p, i) => (i === idx ? txt : p)));
            }}
            multiline
          />
        </View>
      ))}

      <AdminButton
        title="Adicionar item"
        variant="secondary"
        size="compact"
        icon="add-outline"
        onPress={() => setLines((prev) => [...prev, ""])}
        style={{ alignSelf: "flex-start" }}
      />
    </View>
  );

  // Cabeçalho sanfonado de cada frequência na sessão de descrições.
  const renderInfosSection = (key, label, lines, setLines) => (
    <View style={styles.accordion}>
      <Pressable
        onPress={() => setExpandedInfos((p) => ({ ...p, [key]: !p[key] }))}
        style={({ hovered }) => [
          styles.accordionHeader,
          hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
        ]}
      >
        <Ionicons
          name={expandedInfos[key] ? "chevron-down" : "chevron-forward"}
          size={16}
          color={ADMIN_COLORS.muted}
        />
        <Text style={styles.accordionTitle}>{label}</Text>
        <Text style={styles.accordionCount}>
          {lines.length} {lines.length === 1 ? "item" : "itens"}
        </Text>
      </Pressable>
      {expandedInfos[key] ? renderLinesEditor(lines, setLines) : null}
    </View>
  );

  // Campo de preço: o CurrencyInput só cuida da máscara — quem desenha é o
  // AdminInput, como no modal de produto.
  const renderPriceField = ({
    label,
    placeholder,
    hint,
    state,
    onChangeValue,
    onChangeFormatted,
  }) => (
    <Field style={{ marginBottom: 0 }}>
      <FieldLabel required>{label}</FieldLabel>
      {hint ? <Hint>{hint}</Hint> : null}
      <CurrencyInput
        prefix="R$ "
        delimiter="."
        separator=","
        precision={2}
        value={state.value}
        onChangeValue={onChangeValue}
        onChangeText={onChangeFormatted}
        renderTextInput={({ ref, ...inputProps }) => (
          <AdminInput
            {...inputProps}
            placeholder={placeholder}
            editable={!!user?.isAdmin}
          />
        )}
      />
      <FieldError>{state.error}</FieldError>
    </Field>
  );

  // Um link por periodicidade existente na Stripe. Sem domínio configurado a
  // lista sai vazia e o card mostra a orientação de onde preencher.
  const checkoutLinks = [
    { key: "monthly", label: "Mensal", productId: mockedProductIdMonthly },
    { key: "quarterly", label: "Trimestral", productId: mockedProductIdQuarterly },
    { key: "semiannual", label: "Semestral", productId: mockedProductIdSemiannual },
    { key: "yearly", label: "Anual", productId: mockedProductIdYearly },
  ]
    .map((item) => ({
      key: item.key,
      label: item.label,
      url: buildPlanCheckoutUrl(checkoutDomain, item.productId),
    }))
    .filter((item) => !!item.url);

  const selectedModulesCount = tree.reduce(
    (total, cat) => total + cat.cards.filter((card) => card.selected).length,
    0,
  );

  const alertVariant = /❌|erro/i.test(alertState.message || "")
    ? "error"
    : /✅|sucesso/i.test(alertState.message || "")
      ? "success"
      : "info";

  // O ícone do AdminAlert já comunica o estado; o emoji no início da mensagem
  // viraria um segundo símbolo dizendo a mesma coisa.
  const alertMessage = String(alertState.message || "").replace(
    /^[✅❌]\s*/,
    "",
  );

  return (
    <Modal animationType="fade" visible={isVisible} transparent={true}>
      <ModalBackground>
        <ModalContainer>
          <Header>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.modalTitle}>Editar plano</Text>
              <Text style={styles.modalSubtitle} numberOfLines={1}>
                {selectedPlan?.title || "Plano de assinatura"}
              </Text>
            </View>
            <CloseButton
              onPress={() => {
                resetFormFields();
                onClose();
              }}
            >
              <Feather name="x" size={20} color={ADMIN_COLORS.ink} />
            </CloseButton>
          </Header>

          {/* Uma barra de rolagem só: antes havia um ScrollView em volta deste,
              e o modal aparecia com duas. O rodapé fica fora dela, então
              "Salvar" continua à mão em qualquer ponto do formulário. */}
          <Content
            style={{ flexGrow: 1, flexShrink: 1, minHeight: 0 }}
            contentContainerStyle={{ paddingTop: 16, paddingBottom: 8 }}
            showsVerticalScrollIndicator
          >
            <SectionCard
              title="Informações do plano"
              subtitle="Como o plano aparece para o aluno na página de vendas."
            >
              <Field>
                <FieldLabel required>Nome do plano</FieldLabel>
                <AdminInput
                  placeholder="Digitar nome"
                  value={titlePlanValue.value}
                  onChangeText={(text) =>
                    setTitlePlanValue({ value: text, error: "" })
                  }
                />
                <FieldError>{titlePlanValue.error}</FieldError>
              </Field>

              <Field>
                <FieldLabel>Página de vendas (opcional)</FieldLabel>
                <Hint>
                  Se tiver uma página de vendas específica para este plano, cole
                  o link aqui.
                </Hint>
                <AdminInput
                  placeholder="https://..."
                  value={salesPageUrl}
                  onChangeText={(text) => setSalesPageUrl(text)}
                />
              </Field>

              <View style={styles.switchRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <FieldLabel>Plano ativo</FieldLabel>
                  <Hint>
                    Inativo, o plano deixa de aparecer na página de vendas. Quem
                    já assina não é afetado.
                  </Hint>
                </View>
                <View style={styles.switchRight}>
                  <Badge variant={isActive ? "success" : "neutral"}>
                    {isActive ? "Ativo" : "Inativo"}
                  </Badge>
                  {isToggling ? (
                    <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
                  ) : (
                    <AdminSwitch
                      value={isActive}
                      onValueChange={(val) => handleToggleActive(val)}
                      disabled={isToggling}
                    />
                  )}
                </View>
              </View>
            </SectionCard>

            <CatalogVitrineFields
              ref={vitrineRef}
              collectionName="plans"
              docId={selectedPlan?.id}
              name={titlePlanValue.value || selectedPlan?.title}
              accessIds={Object.values(selectedPlan?.productIds || {}).filter(Boolean)}
            />

            <ProductCatalogFilters
              collectionName="plans"
              docId={selectedPlan?.id}
              productName={titlePlanValue.value || selectedPlan?.title}
              accessIds={Object.values(selectedPlan?.productIds || {}).filter(Boolean)}
            />

            {hasOutsideAboutPage ? (
              <CheckoutLinksCard
                subtitle="Um link por periodicidade. Quem abrir vai direto para o cadastro e o pagamento."
                links={checkoutLinks}
                loading={loadingCheckoutDomain}
                hasError={checkoutDomainError}
                onReload={reloadCheckoutDomain}
                emptyMessage="Este plano ainda não tem periodicidades com preço na Stripe. Crie os preços abaixo e salve para gerar os links."
              />
            ) : null}

            <SectionCard
              title="Preços e teste grátis"
              subtitle="Os valores ficam na Stripe. Só mexa aqui para criar preços novos."
            >
              <AdminCheckbox
                value={editPricesEnabled}
                onValueChange={(value) => setEditPricesEnabled(value)}
                label="Quero adicionar novos preços"
              />
              <Hint>
                {editPricesEnabled
                  ? "Ao salvar, a Stripe pode criar novos preços e o teste grátis definido abaixo passa a valer."
                  : "Nenhum preço novo será criado na Stripe e o teste grátis não será alterado."}
              </Hint>

              {editPricesEnabled ? (
                <>
                  {!user?.isAdmin ? (
                    <InfoBanner variant="warn">
                      Você pode visualizar os valores, mas apenas
                      administradores podem editar e salvar preços na Stripe.
                    </InfoBanner>
                  ) : null}

                  <View style={styles.subCard}>
                    <View style={styles.switchRow}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <FieldLabel>Oferecer teste grátis</FieldLabel>
                        <Hint>
                          Período em que o aluno acessa antes da primeira
                          cobrança.
                        </Hint>
                      </View>
                      <AdminSwitch
                        value={trialEnabled}
                        onValueChange={(val) => {
                          setTrialEnabled(val);
                          setTrialError("");
                          if (!val) setTrialDays("0");
                          else if (!trialDays || Number(trialDays) <= 0)
                            setTrialDays("7");
                        }}
                      />
                    </View>

                    {trialEnabled ? (
                      <Field style={{ marginTop: 14, marginBottom: 0 }}>
                        <FieldLabel required>Dias de teste</FieldLabel>
                        <Hint>7 dias é o mais comum.</Hint>
                        <AdminInput
                          placeholder="Ex.: 7"
                          keyboardType="numeric"
                          value={trialDays}
                          onChangeText={(t) => {
                            const onlyNumbers = String(t || "").replace(
                              /[^0-9]/g,
                              "",
                            );
                            setTrialDays(onlyNumbers);
                            setTrialError("");
                          }}
                        />
                        <FieldError>{trialError}</FieldError>
                      </Field>
                    ) : (
                      <Hint style={{ marginTop: 10 }}>
                        Sem teste grátis: a cobrança inicia imediatamente.
                      </Hint>
                    )}
                  </View>

                  <Text style={styles.groupTitle}>Mensal</Text>
                  {renderPriceField({
                    label: "Valor mensal (R$)",
                    placeholder: "Ex.: 120,00",
                    state: pricePlanMonthly,
                    onChangeValue: (val) => {
                      setPricePlanMonthly({ value: val ?? "", error: "" });
                      setPricePlanStripeMonthly(
                        val ? Math.round(val * 100) : undefined,
                      );
                    },
                    onChangeFormatted: (formatted) =>
                      setValuePlanWithRSMonthly(formatted),
                  })}

                  <Text style={styles.groupTitle}>Trimestral</Text>
                  <Row gap={14}>
                    <Col>
                      {renderPriceField({
                        label: "À vista (R$)",
                        placeholder: "Ex.: 300,00",
                        state: pricePlanQuarterlyAtSight,
                        onChangeValue: (val) => {
                          setPricePlanQuarterlyAtSight({
                            value: val ?? "",
                            error: "",
                          });
                          setPricePlanStripeQuarterlyAtSight(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSQuarterlyAtSight(formatted),
                      })}
                    </Col>
                    <Col>
                      {renderPriceField({
                        label: "Parcelado (3x) — valor mensal (R$)",
                        placeholder: "Ex.: 108,00",
                        state: pricePlanQuarterly,
                        onChangeValue: (val) => {
                          setPricePlanQuarterly({ value: val ?? "", error: "" });
                          setPricePlanStripeQuarterly(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSQuarterly(formatted),
                      })}
                    </Col>
                  </Row>

                  <Text style={styles.groupTitle}>Semestral</Text>
                  <Row gap={14}>
                    <Col>
                      {renderPriceField({
                        label: "À vista (R$)",
                        placeholder: "Ex.: 480,00",
                        state: pricePlanSemiannualAtSight,
                        onChangeValue: (val) => {
                          setPricePlanSemiannualAtSight({
                            value: val ?? "",
                            error: "",
                          });
                          setPricePlanStripeSemiannualAtSight(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSSemiannualAtSight(formatted),
                      })}
                    </Col>
                    <Col>
                      {renderPriceField({
                        label: "Parcelado (6x) — valor mensal (R$)",
                        placeholder: "Ex.: 85,00",
                        state: pricePlanSemiannual,
                        onChangeValue: (val) => {
                          setPricePlanSemiannual({ value: val ?? "", error: "" });
                          setPricePlanStripeSemiannual(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSSemiannual(formatted),
                      })}
                    </Col>
                  </Row>

                  <Text style={styles.groupTitle}>Anual</Text>
                  <Row gap={14}>
                    <Col>
                      {renderPriceField({
                        label: "À vista (R$)",
                        placeholder: "Ex.: 700,00",
                        state: pricePlanYearlyAtSight,
                        onChangeValue: (val) => {
                          setPricePlanYearlyAtSight({
                            value: val ?? "",
                            error: "",
                          });
                          setPricePlanStripeYearlyAtSight(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSYearlyAtSight(formatted),
                      })}
                    </Col>
                    <Col>
                      {renderPriceField({
                        label: "Parcelado (12x) — valor mensal (R$)",
                        placeholder: "Ex.: 62,00",
                        state: pricePlanYearly,
                        onChangeValue: (val) => {
                          setPricePlanYearly({ value: val ?? "", error: "" });
                          setPricePlanStripeYearly(
                            val ? Math.round(val * 100) : undefined,
                          );
                        },
                        onChangeFormatted: (formatted) =>
                          setValuePlanWithRSYearly(formatted),
                      })}
                    </Col>
                  </Row>

                  <Hint style={{ marginTop: 14 }}>* Campos obrigatórios</Hint>
                </>
              ) : null}
            </SectionCard>

            {hasOutsideAboutPage === false ? (
              <SectionCard
                title="Descrições por frequência"
                subtitle="Itens que aparecem na página de vendas para cada periodicidade."
              >
                <AdminCheckbox
                  value={editInfosEnabled}
                  onValueChange={(value) => setEditInfosEnabled(value)}
                  label="Quero editar as descrições por frequência"
                />
                <Hint>
                  {editInfosEnabled
                    ? "Ao salvar, as descrições serão atualizadas conforme a edição abaixo."
                    : "A sessão permanece fechada e nada será alterado no banco."}
                </Hint>

                {editInfosEnabled ? (
                  <>
                    {hasMonthly
                      ? renderInfosSection(
                          "monthly",
                          "Mensal",
                          infosMonthlyLines,
                          setInfosMonthlyLines,
                        )
                      : null}
                    {hasQuarterly
                      ? renderInfosSection(
                          "quarterly",
                          "Trimestral",
                          infosQuarterlyLines,
                          setInfosQuarterlyLines,
                        )
                      : null}
                    {hasSemiannual
                      ? renderInfosSection(
                          "semiannual",
                          "Semestral",
                          infosSemiannualLines,
                          setInfosSemiannualLines,
                        )
                      : null}
                    {hasYearly
                      ? renderInfosSection(
                          "yearly",
                          "Anual",
                          infosYearlyLines,
                          setInfosYearlyLines,
                        )
                      : null}
                  </>
                ) : null}
              </SectionCard>
            ) : null}

            <SectionCard
              title="Conteúdo liberado pelo plano"
              subtitle="Marque as categorias e os módulos que o aluno acessa ao assinar."
              right={
                !loadingTree && tree.length ? (
                  <Badge variant={selectedModulesCount ? "info" : "neutral"}>
                    {selectedModulesCount === 1
                      ? "1 módulo"
                      : `${selectedModulesCount} módulos`}
                  </Badge>
                ) : null
              }
            >
              {loadingTree ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
                  <Text style={styles.loadingText}>Carregando categorias…</Text>
                </View>
              ) : tree.length === 0 ? (
                <InfoBanner variant="info">
                  Nenhuma categoria cadastrada ainda.
                </InfoBanner>
              ) : (
                <ScrollView
                  style={styles.treeScroll}
                  contentContainerStyle={{ padding: 4 }}
                  nestedScrollEnabled
                >
                  {tree.map((cat) => {
                    const isExpanded = expandedCategoryIds?.includes(cat.id);
                    return (
                      <View key={cat.id} style={styles.treeGroup}>
                        <View style={styles.treeRow}>
                          <Pressable
                            onPress={() => {
                              setExpandedCategoryIds((prev) =>
                                prev?.includes(cat.id)
                                  ? prev.filter((id) => id !== cat.id)
                                  : [...(prev || []), cat.id],
                              );
                            }}
                            hitSlop={6}
                            disabled={!cat.cards.length}
                            style={styles.treeChevron}
                          >
                            <Ionicons
                              name={
                                isExpanded ? "chevron-down" : "chevron-forward"
                              }
                              size={16}
                              color={
                                cat.cards.length
                                  ? ADMIN_COLORS.muted
                                  : ADMIN_COLORS.border
                              }
                            />
                          </Pressable>

                          <AdminCheckbox
                            value={cat.selected}
                            onValueChange={() => {
                              setTree((t) =>
                                t.map((c) =>
                                  c.id === cat.id
                                    ? {
                                        ...c,
                                        selected: !c.selected,
                                        cards: c.cards.map((cd) => ({
                                          ...cd,
                                          selected: !c.selected,
                                        })),
                                      }
                                    : c,
                                ),
                              );
                            }}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Text style={styles.treeCategory} numberOfLines={1}>
                              {cat.title}
                            </Text>
                          </AdminCheckbox>

                          <Text style={styles.treeCount}>
                            {cat.cards.length}{" "}
                            {cat.cards.length === 1 ? "módulo" : "módulos"}
                          </Text>
                        </View>

                        {isExpanded &&
                          cat.cards.map((cd) => (
                            <AdminCheckbox
                              key={cd.id}
                              value={cd.selected}
                              onValueChange={() => {
                                setTree((t) =>
                                  t.map((c) =>
                                    c.id === cat.id
                                      ? {
                                          ...c,
                                          cards: c.cards.map((cc) =>
                                            cc.id === cd.id
                                              ? { ...cc, selected: !cc.selected }
                                              : cc,
                                          ),
                                        }
                                      : c,
                                  ),
                                );
                              }}
                              style={styles.treeCardRow}
                            >
                              <Text style={styles.treeModule} numberOfLines={1}>
                                {cd.title}
                              </Text>
                            </AdminCheckbox>
                          ))}
                      </View>
                    );
                  })}
                </ScrollView>
              )}
            </SectionCard>
          </Content>

          <View style={styles.footer}>
            {vitrineFeedback ? (
              <AdminAlert
                variant={vitrineFeedback.variant}
                style={{ marginTop: 0, marginBottom: 12 }}
                onClose={() => setVitrineFeedback(null)}
              >
                {vitrineFeedback.message}
              </AdminAlert>
            ) : null}
            {alertState.visible ? (
              <AdminAlert
                variant={alertVariant}
                style={{ marginTop: 0, marginBottom: 12 }}
                onClose={() =>
                  setAlertState({ visible: false, message: null })
                }
              >
                {alertMessage}
              </AdminAlert>
            ) : null}

            <View style={styles.footerActions}>
              <AdminButton
                title="Cancelar"
                variant="secondary"
                disabled={loadingSave}
                onPress={() => {
                  resetFormFields();
                  onClose();
                }}
              />
              <AdminButton
                title="Salvar"
                isLoading={loadingSave}
                onPress={handleEdit}
              />
            </View>
          </View>
        </ModalContainer>
      </ModalBackground>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 18,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  modalSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
  },
  switchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  switchRight: { flexDirection: "row", alignItems: "center", gap: 12 },
  subCard: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 14,
    marginTop: 16,
  },
  groupTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "700",
    color: ADMIN_COLORS.muted,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginTop: 20,
    marginBottom: 8,
  },
  lineCard: {
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    padding: 12,
    marginBottom: 10,
  },
  lineHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  lineTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "600",
    color: ADMIN_COLORS.muted,
  },
  lineRemove: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    cursor: "pointer",
  },
  lineRemoveText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: "600",
    color: ADMIN_COLORS.danger,
  },
  accordion: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    marginTop: 12,
    overflow: "hidden",
  },
  accordionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    cursor: "pointer",
  },
  accordionTitle: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  accordionCount: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
  },
  loadingRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  loadingText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
  },
  treeScroll: {
    maxHeight: 320,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  treeGroup: { paddingVertical: 2 },
  treeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  treeChevron: { padding: 2, cursor: "pointer" },
  treeCategory: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  treeCount: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
  },
  treeCardRow: { paddingVertical: 7, paddingLeft: 42, paddingRight: 8 },
  treeModule: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.label,
  },
  footer: {
    width: "100%",
    padding: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
});

export default EditStripePlanModal;
